import { AfterViewInit, Component, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, Observable, Subject } from 'rxjs';
import { MatDrawer, MatDrawerContainer, MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { delay, filter, map, pairwise, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthAdaptorService } from '@aiwyn/auth';
import { ConfigService, GetBusinessEntities, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import { GetPayables, PaymentsState, SetPaymentEntrySource, UpdateArTranAllocations } from '@aiwyn/common/payments';
import { GetAutoPayClients } from '@aiwyn/common/settings';
import {
  AiwynBreakpoint,
  getFirstOfQueryList,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  ignoreTruthy,
  isEmpty,
  isLoaded,
  LoadableHelper,
  removeDuplicates,
} from '@aiwyn/shared';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  PAYMENT_ENTRY_SOURCE_NAV_STATE_KEY,
  PAYMENT_ENTRY_SOURCE_PAY_INVOICE_EMAIL,
  PAYMENT_LINK_SOURCE_NAV_STATE_KEY,
  ROUTE_TOKEN_INVOICES,
  ROUTE_TOKEN_MAIN_BASE,
} from '../../../routing-constants';
import { StripeApiService } from '@aiwyn/api';

interface ReviewAndPayZeroState {
  title: string;
  text: string;
}

@Component({
  selector: 'app-review-and-pay',
  templateUrl: './review-and-pay.component.html',
  styleUrls: ['./review-and-pay.component.scss'],
  standalone: false,
})
export class ReviewAndPayComponent implements AfterViewInit, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();

  @ViewChild('sidenav') public sidenav: MatSidenav;

  // Variable clientId and invoiceId used to store URL data.
  // Note: URL must use format specified in app-routing.module.ts.
  invoiceIdsQueryParam$ = this.route.queryParams.pipe(
    map((params: { invoiceId: string }) => params.invoiceId ?? ''),
    map(invoiceIds => invoiceIds.split(',')),
    shareReplay(1),
  );

  // Not to get confused with `selectedPayables$`. This represents initial state (based on URL), while the other is based on user input.
  matchingPayables$ = combineLatest([
    getLoadedResults(this.store.select(PaymentsState.getPayablesLoadable)),
    this.invoiceIdsQueryParam$,
  ]).pipe(
    filter(([payables, invoiceIds]) => !isEmpty(payables) && !isEmpty(invoiceIds)),
    map(([payables, invoiceIds]) => {
      const priorityBusinessEntityId = this.store.selectSnapshot(PaymentsState.getBusinessEntityIdWithMostAmountDue);
      const matchingPayables = payables.filter(payable => invoiceIds.includes(`${payable.arTranId}`));
      const selectedBusinessEntityIds = removeDuplicates(matchingPayables.map(({ entityId }) => entityId));
      return selectedBusinessEntityIds.length > 1
        ? matchingPayables.filter(payable => payable.entityId === priorityBusinessEntityId)
        : matchingPayables;
    }),
    shareReplay(1),
  );

  private readonly _hasCompletedSelectionFromUrl$ = new BehaviorSubject(false);
  selectPayablesFromUrl$ = this.matchingPayables$.pipe(
    take(1),
    map(matchingPayables => matchingPayables.map(payable => ({
      arTranId: payable.arTranId,
      allocatedAmount: payable.amountRemaining,
      active: payable.invoice.arTran.active,
    }))),
    // PP-3775: this is the URL-driven auto-selection on page load, not active intent — tag it 'auto'
    // so the PAYMENT_PAYABLES_SELECTED it triggers is excluded from the intent-based funnel start.
    switchMap(arTranAllocations => this.store.dispatch(new UpdateArTranAllocations(arTranAllocations, 'auto'))),
    tap(_ => this._hasCompletedSelectionFromUrl$.next(true)),
    takeUntil(this._unsubscribe$),
  );

  arePayablesLoading$ = this.store.select(PaymentsState.getPayablesStatus).pipe(
    map(status => LoadableHelper.isLoading(status)),
    shareReplay(1),
  );

  selectedPayables$ = this.store.select(PaymentsState.getSelectedPayables);

  areSelectedPayablesEmpty$ = this.arePayablesLoading$.pipe(
    ignoreTruthy(),
    switchMap(_ => this.selectedPayables$),
    map(isEmpty),
  );

  areMatchingPayablesPaid$ = combineLatest([
    this.invoiceIdsQueryParam$,
    this.matchingPayables$,
  ]).pipe(
    map(([invoiceIds, matchingPayables]) => !isEmpty(invoiceIds) && isEmpty(matchingPayables)),
  );
  reviewAndPayZeroState$: Observable<ReviewAndPayZeroState> = this.areSelectedPayablesEmpty$.pipe(
    ignoreFalsy(),
    switchMap(_ => combineLatest([this.areMatchingPayablesPaid$, this.invoiceIdsQueryParam$])),
    map(([areSelectedPayablesPaid, payableIdsFromUrl]) => {
      if (!areSelectedPayablesPaid) {
        return {
          title: 'No invoices found',
          text: 'Please contact your firm finance contact if you feel this is a mistake.',
        };
      }

      return {
        title: `${payableIdsFromUrl.length === 1 ? 'This invoice is' : 'These invoices are'} no longer available.`,
        text: 'If you\'ve recently paid this invoice, it has been processed. You can view your payment history in the Payments tab.',
      };
    })
  );

  onPaymentCompletion$ = this.store.selectOnce(PaymentsState.getLastExecutedPaymentBatchId).pipe(
    map(id => id ?? -1),
    switchMap(lastExecutedPaymentId => this.store.select(PaymentsState.getExecutedPaymentBatch(lastExecutedPaymentId + 1)).pipe(
      ignoreNullish(),
      switchMap(_ => this.store.select(PaymentsState.getExecutedPaymentBatchStatus(lastExecutedPaymentId + 1))),
      isLoaded(),
      switchMap(_ => this.navigateToInvoicesPage()),
    )),
    takeUntil(this._unsubscribe$),
  );

  navigateAwayOnNoPayablesSelected$ = this._hasCompletedSelectionFromUrl$.pipe( // wait until initialization is complete
    ignoreFalsy(),
    take(1),
    switchMap(_ => this.store.select(PaymentsState.getSelectedPayableIds)),
    pairwise(),
    filter(([previousSelection, currentSelection]) => previousSelection?.length > 0 && currentSelection?.length === 0),
    take(1),
    delay(50), // this can fire on sign out, which breaks automatic post-auth redirection
    switchMap(_ => this.navigateToInvoicesPage()),
    takeUntil(this._unsubscribe$),
  );

  //#region Sidebar

  @ViewChild(MatDrawerContainer) matDrawerContainer: MatDrawerContainer;
  @ViewChildren(MatDrawer) matDrawer: QueryList<MatDrawer>;

  isMobile$: Observable<boolean> = this.breakpointObserver.observe([AiwynBreakpoint.SIDEBAR_POPOVER]).pipe(
    map(breakpointMatched => breakpointMatched.matches),
  );

  sideDrawerMode$: Observable<'over' | 'side'> = this.isMobile$.pipe(
    map(isMobile => isMobile ? 'over' : 'side'),
  );

  readonly isSideDrawerOpen$ = new BehaviorSubject<boolean>(undefined);
  private _setIsSideDrawerOpen$ = defer(() => getFirstOfQueryList(this.matDrawer).pipe(
    switchMap(drawer => drawer._animationStarted.pipe(
      map(_ => drawer.opened),
    )),
    tap(isOpened => this.isSideDrawerOpen$.next(isOpened)),
    takeUntil(this._unsubscribe$),
  ));

  onSidebarOpenedChange(isOpened: boolean): void {
    if (isOpened) {
      this.matDrawerContainer.open();
    } else {
      this.matDrawerContainer.close();
    }
  }

  openSidebar(): void {
    this.matDrawerContainer.open();
  }

  //#endregion

  isReviewAndPayEmptyStateMessageEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_REVIEW_AND_PAY_EMPTY_STATE_MESSAGE);

  constructor(
    public authAdaptor: AuthAdaptorService,
    public configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly stripeApiService: StripeApiService,
  ) {
    // PP-3775: resolve the payment entry source before any child (e.g. the sidebar's PAYMENT_SETUP_STARTED)
    // can fire — the parent constructor runs before child components mount. The pay-invoice email link
    // arrives via PayInvoiceAuthGuard's RedirectCommand, which carries the entry source in navigation
    // `state` (not the URL); the in-portal "Review Invoices" button navigates here without it. Reading on
    // every load also clears any stale override from an earlier email-link session, so the two flows that
    // share this URL stay correctly attributed.
    //
    // ART-8293: the guard also carries `linkSource` ('invoice' | 'collections') the same way, letting a
    // new-invoice notification email be isolated from a collections/dunning statement email. Links minted
    // before the backend started encoding `linkSource` carry none — default those to `-invoice`, the
    // historical majority case before the split existed.
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    const isPayInvoiceEmail = navigationState?.[PAYMENT_ENTRY_SOURCE_NAV_STATE_KEY] === PAYMENT_ENTRY_SOURCE_PAY_INVOICE_EMAIL;
    const linkSource = navigationState?.[PAYMENT_LINK_SOURCE_NAV_STATE_KEY];
    const paymentEntrySource = isPayInvoiceEmail
      ? (linkSource === 'collections' ? 'pay-invoice-email-collections' : 'pay-invoice-email-invoice')
      : 'review-and-pay';
    this.store.dispatch(new SetPaymentEntrySource(paymentEntrySource));

    this.store.dispatch([
      new GetPayables(true),
      new GetAutoPayClients(),
      new GetBusinessEntities(defer(() => this.stripeApiService.getBusinessEntities())),
    ]);
    this.selectPayablesFromUrl$.subscribe();

    this.onPaymentCompletion$.subscribe();
    this.navigateAwayOnNoPayablesSelected$.subscribe();
  }

  ngAfterViewInit(): void {
    this._setIsSideDrawerOpen$.subscribe();
  }

  /**
   * Return to the InvoicesComponent.
   */
  onBackClicked(): void {
    this.navigateToInvoicesPage();
  }

  navigateToInvoicesPage(): Promise<boolean> {
    return this.router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_INVOICES}`);
  }

  /**
   * Destroy subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
